/**
 * Envío múltiple — forma interna de los destinos del cotizador y traducción al
 * wire (`envio_destinos[]` de NV/presupuesto). Separado del componente para que
 * el fast refresh de Vite no se queje (un archivo de componente solo exporta
 * componentes). Ver components/CotizadorEnvio.jsx.
 */
export const esRepartible = (sku) => {
  const s = String(sku || '').toUpperCase()
  return s !== '' && !s.startsWith('ENV') && !s.startsWith('GIF')
}

/**
 * Las líneas repartibles del pedido AGRUPADAS por SKU: el mismo artículo puede
 * venir en dos renglones (5 cobrados + 1 bonificado del 6×5). Cantidad = suma;
 * precio = ponderado (así el subtotal del destino es el importe real).
 */
export function lineasRepartibles(lineas) {
  const porSku = new Map()
  for (const l of lineas || []) {
    const sku = String(l.sku || '').toUpperCase()
    const cantidad = parseFloat(l.cantidad) || 0
    if (!esRepartible(sku) || cantidad <= 0) continue
    const prev = porSku.get(sku)
    const importe = (parseFloat(l.precio) || 0) * cantidad
    if (prev) {
      const cant = prev.cantidad + cantidad
      const imp = prev._importe + importe
      porSku.set(sku, { ...prev, cantidad: cant, _importe: imp, precio: cant > 0 ? imp / cant : 0 })
    } else {
      porSku.set(sku, { ...l, sku, cantidad, _importe: importe, precio: cantidad > 0 ? importe / cantidad : 0 })
    }
  }
  return [...porSku.values()].map(l => { const copia = { ...l }; delete copia._importe; return copia })
}

/** Asignado vs pedido por SKU; `exacto` = cada SKU repartido justo y sin SKUs de más. */
export function repartoDe(destinos, lineas) {
  const repartibles = lineasRepartibles(lineas)
  const asignado = {}
  ;(destinos || []).forEach(d => Object.entries(d.items || {}).forEach(([sku, c]) => { asignado[sku] = (asignado[sku] || 0) + (parseFloat(c) || 0) }))
  const filas = repartibles.map(l => ({ ...l, asignado: asignado[l.sku] || 0 }))
  const sobrantes = Object.keys(asignado).filter(sku => asignado[sku] > 0 && !repartibles.some(l => l.sku === sku))
  return {
    filas,
    exacto: filas.length > 0 && sobrantes.length === 0 && filas.every(f => Math.abs(f.asignado - f.cantidad) < 0.001),
    pedido: filas.reduce((a, f) => a + f.cantidad, 0),
    asignado: filas.reduce((a, f) => a + f.asignado, 0),
  }
}

export const nuevoDestino = (extra = {}) => ({
  _key: Math.random().toString(36).slice(2),
  receptor_nombre: '', telefono: '', calle: '', piso: '', localidad: '', provincia: '', codigo_postal: '', notas: '',
  items: {},        // { [sku]: cantidad }
  cotizacion: null, // resultado de /envios/cotizar para este destino
  ...extra,
})

/** Forma interna → lo que esperan NV/presupuesto (`envio_destinos[]`). */
export function destinosParaPayload(destinos, lineas) {
  const porSku = Object.fromEntries(lineasRepartibles(lineas).map(l => [l.sku, l]))
  return (destinos || []).map(d => ({
    receptor_nombre: d.receptor_nombre?.trim() || null,
    telefono: d.telefono?.trim() || null,
    calle: d.calle?.trim() || null,
    numero: null,
    piso: d.piso?.trim() || null,
    depto: null,
    localidad: d.localidad?.trim() || null,
    provincia: d.provincia?.trim() || null,
    codigo_postal: d.codigo_postal?.trim() || null,
    notas: d.notas?.trim() || null,
    items: Object.entries(d.items || {})
      .filter(([sku, c]) => (parseFloat(c) || 0) > 0 && porSku[sku])   // solo SKUs vigentes del pedido
      .map(([sku, c]) => ({
        articulo_id: porSku[sku]?.articulo_id ?? null,
        sku,
        cantidad: parseFloat(c),
        precio: porSku[sku]?.precio ?? null,
      })),
    cotizacion: d.cotizacion ? {
      zona: d.cotizacion.zona, label: d.cotizacion.label, costo: d.cotizacion.costo,
      gratis: !!d.cotizacion.gratis, pendiente: !!d.cotizacion.pendiente, mensaje: d.cotizacion.mensaje,
    } : null,
  }))
}

/** Lo que devuelve la API (`envio_destinos`) → forma interna. */
export function destinosDesdeApi(apiDestinos) {
  return (apiDestinos || []).map(d => nuevoDestino({
    receptor_nombre: d.receptor_nombre || '', telefono: d.telefono || '',
    calle: [d.calle, d.numero].filter(Boolean).join(' '),
    piso: [d.piso, d.depto].filter(Boolean).join(' '),
    localidad: d.localidad || '', provincia: d.provincia || '', codigo_postal: d.codigo_postal || '', notas: d.notas || '',
    // El mismo SKU puede venir repetido: se suma (no se pisa).
    items: (d.items || []).reduce((acc, it) => { const k = String(it.sku).toUpperCase(); acc[k] = (acc[k] || 0) + Number(it.cantidad); return acc }, {}),
    cotizacion: d.cotizacion || null,
  }))
}
